import { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import Swal from "sweetalert2";
import { login as loginApi } from "../services/authService";
import { aceptarTratamientoDatos } from "../services/usuarioService";
import TratamientoDatosDialog from "../components/TratamientoDatosDialog";
import {
  getPrivateRouteByRole,
  getUserId,
  hasAceptadoTratamientoDatos,
} from "../utils/authUser";
import {
  clearStoredSession,
  isTokenValid,
  redirectAfterSessionExpired,
} from "../utils/session";

export const AuthContext = createContext();

// Límite máximo permitido por setTimeout (~24.8 días); evita overflow con exp lejanos.
const MAX_TIMEOUT_MS = 2147483647;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiresTratamientoDatos, setRequiresTratamientoDatos] = useState(false);
  const [acceptingTratamiento, setAcceptingTratamiento] = useState(false);
  const expirationTimerRef = useRef(null);

  const clearExpirationTimer = useCallback(() => {
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
  }, []);

  const scheduleExpirationRedirect = useCallback((token) => {
    clearExpirationTimer();

    try {
      const { exp } = jwtDecode(token);
      if (!exp) return;

      const msUntilExpiration = exp * 1000 - Date.now();
      if (msUntilExpiration <= 0) {
        clearStoredSession();
        redirectAfterSessionExpired();
        return;
      }

      expirationTimerRef.current = setTimeout(() => {
        if (!isTokenValid(localStorage.getItem("token"))) {
          clearStoredSession();
          redirectAfterSessionExpired();
        }
      }, Math.min(msUntilExpiration, MAX_TIMEOUT_MS));
    } catch (error) {
      console.error("Error al programar expiración de sesión", error);
    }
  }, [clearExpirationTimer]);

  useEffect(() => clearExpirationTimer, [clearExpirationTimer]);

  const applySession = (decoded, token) => {
    if (!isTokenValid(token)) {
      clearStoredSession();
      setUser(null);
      setIsAuthenticated(false);
      setRequiresTratamientoDatos(false);
      clearExpirationTimer();
      return false;
    }

    setUser(decoded);
    setIsAuthenticated(true);
    setRequiresTratamientoDatos(!hasAceptadoTratamientoDatos(decoded));
    scheduleExpirationRedirect(token);
    return true;
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      if (!isTokenValid(storedToken)) {
        clearStoredSession();
        return;
      }

      try {
        applySession(JSON.parse(storedUser), storedToken);
      } catch (error) {
        console.error("Error al restaurar la sesión", error);
        clearStoredSession();
      }
    }
  }, []);

  const redirectByRole = (sessionUser) => {
    window.location.replace(getPrivateRouteByRole(sessionUser?.idRol));
  };

  const login = async (correo, contraseña) => {
    try {
      const data = await loginApi(correo, contraseña);

      if (data.success) {
        try {
          const decoded = jwtDecode(data.token);

          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(decoded));
          applySession(decoded, data.token);

          if (hasAceptadoTratamientoDatos(decoded)) {
            redirectByRole(decoded);
          }
        } catch (error) {
          console.error("Error al decodificar el token", error);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.message,
        });
      }
    } catch (error) {
      console.error("Error en login:", error);
    }
  };

  const handleAcceptTratamientoDatos = async () => {
    const userId = getUserId(user);
    if (!userId) {
      Swal.fire("Error", "No se encontró el id del usuario autenticado.", "error");
      return;
    }

    try {
      setAcceptingTratamiento(true);
      const response = await aceptarTratamientoDatos(userId);

      if (response?.success) {
        const updatedUser = { ...user, AutoTraDatos: true };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setRequiresTratamientoDatos(false);
        redirectByRole(updatedUser);
        return;
      }

      Swal.fire(
        "Error",
        response?.message || "No fue posible registrar la aceptación del tratamiento de datos.",
        "error"
      );
    } catch (error) {
      console.error("Error al aceptar tratamiento de datos:", error);

      if (error?.response?.status === 401) {
        return;
      }

      const serverMessage = error?.response?.data?.message || error?.response?.data?.messages?.error;
      Swal.fire(
        "Error",
        serverMessage || "Error en la conexión con el servidor.",
        "error"
      );
    } finally {
      setAcceptingTratamiento(false);
    }
  };

  const logout = () => {
    clearExpirationTimer();
    clearStoredSession();
    setUser(null);
    setIsAuthenticated(false);
    setRequiresTratamientoDatos(false);
    window.location.replace("/login");
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setRequiresTratamientoDatos(!hasAceptadoTratamientoDatos(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        requiresTratamientoDatos,
        login,
        logout,
        updateUser,
        acceptTratamientoDatos: handleAcceptTratamientoDatos,
      }}
    >
      {children}
      <TratamientoDatosDialog
        open={isAuthenticated && requiresTratamientoDatos}
        onAccept={handleAcceptTratamientoDatos}
        loading={acceptingTratamiento}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
